// Plays the sound bank's effects (bank.ts): one buffer per play, a slight random pitch and level variation so a
// repeated sound never sounds like a loop, an optional place in the stereo field (space.ts), a reverb send, and
// ambience ducking for the sounds that must cut through. A per-sound rate limit and a voice cap keep busy races sane.
import { state } from '../data/store.js';
import { audio, duck, masterVolume, whenAudio, type Bus } from './engine.js';
import { SOUNDS, bankBuffer, loadSound, prepareBank, type BusName } from './bank.js';
import type { Place } from './space.js';

/** Same sound restarted sooner than this (s) is dropped: ten simultaneous hits would just be louder noise. */
export const RETRIGGER_S = 0.045;
/** Simultaneous voices beyond this are dropped. */
export const MAX_VOICES = 40;
/** A sound still rendering is played if it becomes ready within this delay (s); later it would be out of sync. */
const LATE_S = 0.12;
/** Placed sounds quieter than this are not worth a voice. */
const INAUDIBLE = 0.02;

const lastAt = new Map<string, number>();
let voices = 0;

/** Whether a sound may start now, given when it last started and how many voices play (pure, for tests). */
export const mayPlay = (now: number, last: number | undefined, active: number): boolean =>
  active < MAX_VOICES && (last === undefined || now - last >= RETRIGGER_S);

/** Playback rate and level variation of one play: `r1`, `r2` uniform in [0, 1). Pitch within ±spread, level within ±1 dB. */
export function variation(spread: number, r1: number, r2: number): { rate: number; gain: number } {
  const s = Math.max(0, Math.min(0.2, spread || 0));
  return { rate: 1 + (r1 * 2 - 1) * s, gain: s > 0 ? Math.pow(10, ((r2 * 2 - 1) * 1) / 20) : 1 };
}

// the bank renders as soon as the audio starts: by the countdown every sound is ready
whenAudio(b => { prepareBank(b.ac.sampleRate, b.noise).catch(() => {}); });

export interface PlayOpts { place?: Place | null; bus?: BusName; gain?: number }

function start(b: Bus, name: string, buf: AudioBuffer, o: PlayOpts) {
  const def = SOUNDS[name], v = variation(def.vary, Math.random(), Math.random()), ac = b.ac, t = ac.currentTime;
  const src = ac.createBufferSource(), g = ac.createGain();
  src.buffer = buf; src.playbackRate.value = v.rate;
  g.gain.value = v.gain * (o.gain ?? 1) * (o.place?.gain ?? 1);
  let head: AudioNode = src;
  if (o.place && o.place.lp < 18000) { const f = ac.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = o.place.lp; f.Q.value = 0.5; src.connect(f); head = f; }
  head.connect(g);
  let tail: AudioNode = g;
  if (o.place?.pan) { const p = ac.createStereoPanner(); p.pan.value = o.place.pan; g.connect(p); tail = p; }
  tail.connect(b[o.bus ?? def.bus]);
  // distant sounds are wetter: further away, less direct sound reaches you than reflections
  const wet = def.verb * (o.place ? 1 + (1 - o.place.gain) * 0.8 : 1);
  if (wet > 0) { const s = ac.createGain(); s.gain.value = Math.min(2, wet); tail.connect(s).connect(b.verb); }
  if (def.duck && !o.place) duck(b, def.duck, buf.duration * 0.6);
  voices++;
  src.onended = () => { voices--; src.disconnect(); };
  src.start(t);
}

/** Plays a sound of the bank, centred or at a place; unknown names are ignored. */
export function sfx(name: string, opts: PlayOpts = {}) {
  if (!name || !SOUNDS[name] || masterVolume(state.settings.volume) <= 0) return;
  if (opts.place && opts.place.gain < INAUDIBLE) return;
  const b = audio();
  if (!b) return;
  const t0 = b.ac.currentTime;
  if (!mayPlay(t0, lastAt.get(name), voices)) return;
  lastAt.set(name, t0);
  const buf = bankBuffer(name);
  if (buf) { start(b, name, buf, opts); return; }
  loadSound(name, b.ac.sampleRate, b.noise).then(ready => { if (ready && b.ac.currentTime - t0 <= LATE_S) start(b, name, ready, opts); }).catch(() => {});
}
